const {
  defaultLocale,
  getLocale,
  isSupportedLocale,
  legacyLocale,
  localeCodes,
  storageKey,
} = require('./config');

const externalUrlPattern = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i;

const localizedTagSlugs = [
  { ar: 'إتاحة-الوصول', en: 'accessibility' },
  { ar: 'css', en: 'css' },
  { ar: 'السمات', en: 'theming' },
  { ar: 'الوضع-الداكن', en: 'dark-mode' },
  { ar: 'word-press', en: 'word-press' },
  { ar: 'docker', en: 'docker' },
];

const splitPath = value => {
  const input = value || '/';
  const hashIndex = input.indexOf('#');
  const hash = hashIndex >= 0 ? input.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? input.slice(0, hashIndex) : input;
  const searchIndex = withoutHash.indexOf('?');
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : '';
  const pathname = searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash;

  return { pathname: pathname || '/', search, hash };
};

const getLocaleFromPath = value => {
  const { pathname } = splitPath(value);
  const firstSegment = pathname.split('/').filter(Boolean)[0];

  return isSupportedLocale(firstSegment) ? firstSegment : null;
};

const stripLocaleFromPath = pathname => {
  const segments = pathname.split('/').filter(Boolean);

  if (isSupportedLocale(segments[0])) {
    segments.shift();
  }

  if (!segments.length) {
    return '/';
  }

  const trailingSlash = pathname.endsWith('/') ? '/' : '';
  return `/${segments.join('/')}${trailingSlash}`;
};

const localizeContentPath = (pathname, locale) => {
  const tagMatch = pathname.match(/^\/pensieve\/tags\/([^/]+)(\/?)$/);

  if (!tagMatch) {
    return pathname;
  }

  const tagSlug = decodeURIComponent(tagMatch[1]);
  const localizedTag = localizedTagSlugs.find(tag => tag.ar === tagSlug || tag.en === tagSlug);

  if (!localizedTag) {
    return pathname;
  }

  return `/pensieve/tags/${localizedTag[locale]}${tagMatch[2]}`;
};

const localizePath = (value, locale) => {
  if (externalUrlPattern.test(value || '')) {
    return value;
  }

  const activeLocale = isSupportedLocale(locale) ? locale : defaultLocale;
  const normalizedValue = value && value.startsWith('#') ? `/${value}` : value;
  const { pathname, search, hash } = splitPath(normalizedValue);
  const localizedPathname = localizeContentPath(stripLocaleFromPath(pathname), activeLocale);
  const localePrefix = `/${activeLocale}`;
  const path =
    localizedPathname === '/' ? `${localePrefix}/` : `${localePrefix}${localizedPathname}`;

  return `${path}${search}${hash}`;
};

const detectLocale = ({ savedLocale, browserLanguages = [] } = {}) => {
  if (isSupportedLocale(savedLocale)) {
    return savedLocale;
  }

  const primaryLanguage = browserLanguages[0] || '';
  const browserLocale = primaryLanguage.toLowerCase().split('-')[0];

  return isSupportedLocale(browserLocale) ? browserLocale : defaultLocale;
};

const resolveRootRedirect = ({ savedLocale, browserLanguages, search = '', hash = '' } = {}) => {
  const locale = detectLocale({ savedLocale, browserLanguages });
  return `/${locale}/${search}${hash}`;
};

const isLocalizedHomePath = value => {
  const { pathname } = splitPath(value);
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;

  return localeCodes.some(locale => normalizedPath === `/${locale}`);
};

const getDocumentAttributes = pathname => {
  const isRootPath = splitPath(pathname).pathname === '/';
  const locale = getLocaleFromPath(pathname) || (isRootPath ? defaultLocale : legacyLocale);
  const { lang, dir } = getLocale(locale);

  return { lang, dir };
};

const createRootRedirectScript = () => `(function () {
  var savedLocale = null;
  try {
    savedLocale = window.localStorage.getItem('${storageKey}');
  } catch (error) {}
  var supportedLocales = ${JSON.stringify(localeCodes)};
  var locale = supportedLocales.indexOf(savedLocale) >= 0 ? savedLocale : null;
  if (!locale) {
    var browserLanguages = window.navigator.languages || [window.navigator.language || ''];
    var browserLocale = String(browserLanguages[0] || '').toLowerCase().split('-')[0];
    locale = supportedLocales.indexOf(browserLocale) >= 0 ? browserLocale : '${defaultLocale}';
  }
  window.location.replace('/' + locale + '/' + window.location.search + window.location.hash);
})();`;

module.exports = {
  createRootRedirectScript,
  detectLocale,
  getDocumentAttributes,
  getLocaleFromPath,
  isLocalizedHomePath,
  localizePath,
  resolveRootRedirect,
};
